import { apiErrorMessage } from '@agos/api-client'
import {
  getGetCouponsQueryKey,
  getGetDiscountPresetsQueryKey,
  useDeleteCouponsCouponId,
  useDeleteDiscountPresetsPresetId,
  useGetCoupons,
  useGetDiscountPresets,
  useGetDiscountsStats,
  usePatchCouponsCouponId,
  usePatchDiscountPresetsPresetId,
  usePostCoupons,
  usePostDiscountPresets,
} from '@agos/api-client/menu'
import type { Coupon, DiscountPreset, ManualDiscountEntry } from '@agos/api-client/model'
import { OrderNumberLink, T, formatCount, formatDateTime, formatMoney, tt } from '@agos/shared'
import {
  ConfirmDialog,
  DataTable,
  FilterBar,
  FilterSelect,
  PageHeader,
  StatCard,
  StatusBadge,
  TabGroup,
  cn,
  toast,
  type Column, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  BarChart3,
  Banknote,
  Edit2,
  Percent,
  Plus,
  RefreshCw,
  Tag,
  Trash2,
  TrendingDown,
  Users,
  X,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  COUPON_PERIODS,
  couponPeriodLabel,
  couponRange,
  couponStatsParams,
  type CouponPeriod,
  type CouponRange,
} from './coupon-period'
import {
  couponOrigin,
  discountShare,
  originLabel,
  couponStatus,
  discountType,
  generateCouponCode,
  rankTopCoupons,
  type RankedCoupon,
} from './coupon-status'

/**
 * Ported from legacy `pages/coupons.tsx`. Spec: `docs/pages/manager-menu/coupons.md`.
 *
 * Three tabs: codes a customer types, presets a member of staff applies at the
 * till, and what both cost the business.
 */

const FIELD_CLASSES =
  'w-full px-3 py-2.5 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'
const LABEL_CLASSES = 'text-xs font-semibold text-muted-foreground block mb-1'

const LOYALTY_LEVELS = ['silver', 'gold', 'gold_master', 'elite_master'] as const

interface CouponFormState {
  code: string
  type: 'percent' | 'fixed'
  value: string
  minOrderAmount: string
  maxUses: string
  expiresAt: string
  active: boolean
  minLoyaltyLevel: string
}

const emptyCouponForm = (): CouponFormState => ({
  code: generateCouponCode(),
  type: 'percent',
  value: '',
  minOrderAmount: '',
  maxUses: '',
  expiresAt: '',
  active: true,
  minLoyaltyLevel: '',
})

export function CouponsPage() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'coupons' | 'manual' | 'stats'>('coupons')

  const { data: coupons } = useGetCoupons()
  const { data: presets } = useGetDiscountPresets()
  // FB-029: legacy fetches the statistics once, on the first visit to either
  // tab, and never again. Leave the page open across a shift and the discount
  // figures are as stale as when you arrived.
  // §9.296 - the owner asked for a day filter. `all` is the default, so the tab opens showing what it
  // showed before the period existed.
  const [period, setPeriod] = useState<CouponPeriod>('all')
  const [customRange, setCustomRange] = useState<CouponRange>({ from: '', to: '' })
  const statsParams = couponStatsParams(couponRange(period, new Date(), customRange))
  const {
    data: stats,
    refetch: refetchStats,
    isFetching: isFetchingStats,
  } = useGetDiscountsStats(statsParams, {
    query: { enabled: activeTab === 'stats' || activeTab === 'manual' },
  })

  const createCoupon = usePostCoupons()
  const updateCoupon = usePatchCouponsCouponId()
  const removeCoupon = useDeleteCouponsCouponId()
  const createPreset = usePostDiscountPresets()
  const updatePreset = usePatchDiscountPresetsPresetId()
  const removePreset = useDeleteDiscountPresetsPresetId()

  const [couponSearch, setCouponSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [couponForm, setCouponForm] = useState<CouponFormState | null>(null)
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null)
  const [pendingCouponDeletion, setPendingCouponDeletion] = useState<Coupon | null>(null)

  const [presetForm, setPresetForm] = useState<{
    label: string
    type: 'percent' | 'fixed'
    value: string
    active: boolean
  } | null>(null)
  const [editingPreset, setEditingPreset] = useState<DiscountPreset | null>(null)
  const [pendingPresetDeletion, setPendingPresetDeletion] = useState<DiscountPreset | null>(null)

  const refreshCoupons = () => queryClient.invalidateQueries({ queryKey: getGetCouponsQueryKey() })
  const refreshPresets = () =>
    queryClient.invalidateQueries({ queryKey: getGetDiscountPresetsQueryKey() })

  /**
   * Status is derived once per coupon and carried on the row, so the filter and
   * the badge cannot disagree. Legacy re-reads the clock inside every call,
   * which lets a coupon be filtered as active and badged as expired in the same
   * paint.
   */
  const visibleCoupons = useMemo(() => {
    const now = new Date()
    const query = couponSearch.trim().toLowerCase()

    return (coupons ?? [])
      .map((coupon) => ({ ...coupon, derivedStatus: couponStatus(coupon, now) }))
      .filter((row) => !query || row.code?.toLowerCase().includes(query))
      .filter((row) => statusFilter === 'all' || row.derivedStatus.status === statusFilter)
  }, [coupons, couponSearch, statusFilter])

  type CouponRow = (typeof visibleCoupons)[number]

  const saveCoupon = () => {
    if (!couponForm) return
    if (!couponForm.code.trim() || !couponForm.value) {
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: tt('Kód a hodnota sú povinné.'),
      })
      return
    }

    const shared = {
      code: couponForm.code.trim().toUpperCase(),
      type: couponForm.type,
      value: Number(couponForm.value),
      minOrderAmount: couponForm.minOrderAmount ? Number(couponForm.minOrderAmount) : 0,
      active: couponForm.active,
      minLoyaltyLevel: couponForm.minLoyaltyLevel || null,
    }

    /**
     * Create omits the optional fields so the backend applies its defaults;
     * edit sends explicit nulls so they can be cleared. Swapping the two breaks
     * one case each way — ported as legacy has it.
     */
    const onSuccess = () => {
      void refreshCoupons()
      setCouponForm(null)
      setEditingCoupon(null)
      toast({ title: editingCoupon ? tt('Kupón aktualizovaný') : tt('Kupón vytvorený') })
    }
    const onError = (caught: unknown) =>
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Nepodarilo sa uložiť kupón.')),
      })

    if (editingCoupon?.id !== undefined) {
      updateCoupon.mutate(
        {
          couponId: editingCoupon.id,
          data: {
            ...shared,
            maxUses: couponForm.maxUses ? Number.parseInt(couponForm.maxUses, 10) : null,
            expiresAt: couponForm.expiresAt || null,
          },
        },
        { onSuccess, onError },
      )
      return
    }

    createCoupon.mutate(
      {
        data: {
          ...shared,
          ...(couponForm.maxUses
            ? { maxUses: Number.parseInt(couponForm.maxUses, 10) }
            : {}),
          ...(couponForm.expiresAt ? { expiresAt: couponForm.expiresAt } : {}),
        },
      },
      { onSuccess, onError },
    )
  }

  const savePreset = () => {
    if (!presetForm) return
    if (!presetForm.label.trim() || !presetForm.value) {
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: tt('Popis a hodnota sú povinné.'),
      })
      return
    }

    const data = {
      label: presetForm.label.trim(),
      type: presetForm.type,
      value: Number(presetForm.value),
      active: presetForm.active,
    }

    const onSuccess = () => {
      void refreshPresets()
      setPresetForm(null)
      setEditingPreset(null)
      toast({ title: editingPreset ? tt('Preset aktualizovaný') : tt('Preset vytvorený') })
    }
    const onError = (caught: unknown) =>
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Nepodarilo sa uložiť preset.')),
      })

    if (editingPreset?.id !== undefined) {
      updatePreset.mutate({ presetId: editingPreset.id, data }, { onSuccess, onError })
    } else {
      createPreset.mutate({ data }, { onSuccess, onError })
    }
  }

  const formatDiscount = (type: string | undefined, value: number | undefined) =>
    discountType(type) === 'percent' ? `${value ?? 0} %` : formatMoney(value)

  const couponColumns: Column<CouponRow>[] = [
    {
      key: 'code',
      header: tt('Kód'),
      sortable: true,
      sortValue: (row) => row.code ?? '',
      render: (row) => <span className="font-mono font-bold text-foreground">{row.code}</span>,
    },
    {
      key: 'value',
      header: tt('Zľava'),
      sortable: true,
      sortValue: (row) => row.value ?? 0,
      render: (row) => (
        <span className="font-semibold text-primary">{formatDiscount(row.type, row.value)}</span>
      ),
    },
    {
      key: 'minOrderAmount',
      header: tt('Min. objednávka'),
      sortable: true,
      sortValue: (row) => row.minOrderAmount ?? 0,
      render: (row) =>
        (row.minOrderAmount ?? 0) > 0 ? (
          <span className="text-sm">{formatMoney(row.minOrderAmount)}</span>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        ),
    },
    {
      key: 'uses',
      header: tt('Použitia'),
      sortable: true,
      sortValue: (row) => row.usedCount ?? 0,
      render: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.usedCount ?? 0}
          {row.maxUses != null ? ` / ${row.maxUses}` : ''}
        </span>
      ),
    },
    {
      key: 'expiresAt',
      header: tt('Platnosť do'),
      sortable: true,
      sortValue: (row) => row.expiresAt ?? '',
      render: (row) =>
        row.expiresAt ? (
          <span className="text-sm">{formatDateTime(row.expiresAt).split(',')[0]}</span>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        ),
    },
    {
      // §9.294 - the owner asked where a coupon came from. `Neznámy pôvod` for the 3 518 that
      // predate the column, because null is not the same as hand-typed.
      key: 'origin',
      header: tt('Pôvod'),
      sortable: true,
      sortValue: (row) => couponOrigin(row),
      render: (row) => (
        <span className="text-xs text-muted-foreground" data-coupon-origin={row.originKind ?? 'unknown'}>
          {couponOrigin(row)}
        </span>
      ),
    },
    {
      key: 'status',
      header: tt('Stav'),
      sortable: true,
      sortValue: (row) => row.derivedStatus.status,
      render: (row) => (
        <StatusBadge label={row.derivedStatus.label} color={row.derivedStatus.color} />
      ),
    },
    {
      key: 'actions',
      header: '',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            aria-label={tt('Prepnúť aktivitu')}
            onClick={() =>
              row.id !== undefined &&
              updateCoupon.mutate(
                { couponId: row.id, data: { active: !row.active } },
                {
                  onSuccess: () => void refreshCoupons(),
                  // FB-030: legacy's catch here is empty, so a network failure
                  // leaves the switch where it was and says nothing.
                  onError: (caught) =>
                    toast({
                      variant: 'destructive',
                      title: tt('Chyba'),
                      description: apiErrorMessage(caught, tt('Zmena sa neuložila.')),
                    }),
                },
              )
            }
            className={cn(
              'w-9 h-5 rounded-full transition-all relative',
              row.active ? 'bg-primary' : 'bg-muted',
            )}
          >
            <span
              className={cn(
                'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all',
                row.active ? 'left-4' : 'left-0.5',
              )}
            />
          </button>
          <button
            type="button"
            aria-label={tt('Upraviť')}
            onClick={() => {
              setEditingCoupon(row)
              setCouponForm({
                code: row.code ?? '',
                type: discountType(row.type),
                value: String(row.value ?? ''),
                minOrderAmount: (row.minOrderAmount ?? 0) > 0 ? String(row.minOrderAmount) : '',
                maxUses: row.maxUses != null ? String(row.maxUses) : '',
                expiresAt: row.expiresAt ? (row.expiresAt.split('T')[0] ?? '') : '',
                active: Boolean(row.active),
                minLoyaltyLevel: row.minLoyaltyLevel ?? '',
              })
            }}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            aria-label={tt('Zmazať')}
            onClick={() => setPendingCouponDeletion(row)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ]

  /**
   * §9.144 — the manual-discount log, as legacy renders it and as this port did not.
   *
   * `manualDiscountType` / `manualDiscountValue` stay two columns rather than one *"10%"* cell:
   * ten percent off and ten euros off are different decisions, and the log is read to tell them
   * apart. `manualDiscountOperator` is the column that makes it a log rather than a total — the dump
   * has whole orders given away at 100%, and until now the screen showed the amount and no name.
   *
   * §9.143 — the order number links to the archive, which is the owner's point 36.
   */
  const manualHistoryColumns: Column<ManualDiscountEntry>[] = [
    {
      key: 'order',
      header: tt('Objednávka'),
      sortable: true,
      sortValue: (row) => row.orderNumber ?? '',
      render: (row) => (
        <OrderNumberLink
          orderId={row.id}
          className="font-mono text-xs text-primary hover:underline"
          title={`${tt('Otvoriť v archíve')}: ${row.orderNumber ?? ''}`}
        >
          {row.orderNumber}
        </OrderNumberLink>
      ),
    },
    {
      key: 'date',
      header: tt('Dátum'),
      sortable: true,
      sortValue: (row) => row.createdAt ?? '',
      render: (row) => (
        <span className="text-muted-foreground">
          {row.createdAt ? formatDateTime(row.createdAt) : ''}
        </span>
      ),
    },
    {
      key: 'type',
      header: tt('Typ'),
      render: (row) => (
        <StatusBadge
          label={row.manualDiscountType === 'percent' ? tt('Percento') : tt('Pevná')}
          color={row.manualDiscountType === 'percent' ? 'blue' : 'amber'}
        />
      ),
    },
    {
      key: 'value',
      header: tt('Hodnota'),
      sortable: true,
      sortValue: (row) => row.manualDiscountValue ?? 0,
      render: (row) => (
        <span className="font-semibold">
          {row.manualDiscountType === 'percent'
            ? `${row.manualDiscountValue ?? 0} %`
            : formatMoney(row.manualDiscountValue)}
        </span>
      ),
    },
    {
      key: 'amount',
      header: tt('Suma zľavy'),
      sortable: true,
      sortValue: (row) => row.discountAmount ?? 0,
      render: (row) => (
        <span className="font-semibold text-red-400">−{formatMoney(row.discountAmount)}</span>
      ),
    },
    {
      key: 'operator',
      header: tt('Operátor'),
      render: (row) => (
        <span className="text-muted-foreground" data-discount-operator>
          {row.manualDiscountOperator ?? '-'}
        </span>
      ),
    },
  ]

  /**
   * §9.284, owner O22 — *Najpoužívanejšie kupóny* as a table rather than three spread spans.
   *
   * `rank` is carried on the row and not taken from `render`'s index: `DataTable` numbers the
   * *sorted and paged* rows, so a badge built from it would renumber 1…n on every sort and start
   * again at 1 on page two. The rank is the server's ordering, which is what the heading claims.
   */
  const rankedTopCoupons = rankTopCoupons(stats?.topCoupons)

  const topCouponColumns: Column<RankedCoupon>[] = [
    {
      key: 'rank',
      header: tt('Poradie'),
      sortable: true,
      sortValue: (row) => row.rank,
      className: 'w-16',
      render: (row) => (
        <span
          data-top-coupon-rank={row.rank}
          className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center"
        >
          {row.rank}
        </span>
      ),
    },
    {
      key: 'code',
      header: tt('Kód'),
      sortable: true,
      sortValue: (row) => row.code ?? '',
      render: (row) => <span className="font-mono font-bold">{row.code}</span>,
    },
    {
      key: 'value',
      header: tt('Zľava'),
      sortable: true,
      // Percentages and euro amounts in one column cannot be ordered against each other, so the
      // sort is on the *number*, which is what the reader is comparing within each kind.
      sortValue: (row) => row.value ?? 0,
      className: 'text-right',
      headerClassName: 'text-right',
      render: (row) => (
        <span className="text-primary font-semibold">{formatDiscount(row.type, row.value)}</span>
      ),
    },
    {
      key: 'usage',
      header: tt('Použití'),
      sortable: true,
      sortValue: (row) => row.usageCount ?? 0,
      className: 'text-right',
      headerClassName: 'text-right',
      render: (row) => (
        <span className="text-muted-foreground">
          {row.usageCount}× <T>použité</T>
        </span>
      ),
    },
  ]

  const presetColumns: Column<DiscountPreset>[] = [
    {
      key: 'label',
      header: tt('Popis'),
      sortable: true,
      sortValue: (row) => row.label ?? '',
      render: (row) => (
        <span className="font-semibold text-foreground">
          <T>{row.label}</T>
        </span>
      ),
    },
    {
      key: 'value',
      header: tt('Zľava'),
      sortable: true,
      sortValue: (row) => row.value ?? 0,
      render: (row) => (
        <span className="font-semibold text-primary">{formatDiscount(row.type, row.value)}</span>
      ),
    },
    {
      key: 'active',
      header: tt('Stav'),
      sortable: true,
      sortValue: (row) => (row.active ? 1 : 0),
      render: (row) => (
        <StatusBadge
          label={row.active ? tt('Aktívny') : tt('Neaktívny')}
          color={row.active ? 'green' : 'zinc'}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            aria-label={tt('Upraviť')}
            onClick={() => {
              setEditingPreset(row)
              setPresetForm({
                label: row.label ?? '',
                type: discountType(row.type),
                value: String(row.value ?? ''),
                active: Boolean(row.active),
              })
            }}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            aria-label={tt('Zmazať')}
            onClick={() => setPendingPresetDeletion(row)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={tt('Kupóny a zľavy')}
        description={tt('Kupónové kódy, manuálne zľavy na kase a ich dopad na tržbu.')}
        icon={<Tag className="w-5 h-5" />}
      />

      <TabGroup
        variant="bar"
        active={activeTab}
        onChange={(key) => setActiveTab(key as typeof activeTab)}
        tabs={[
          { id: 'coupons', label: tt('Kupóny'), icon: Tag },
          { id: 'manual', label: tt('Manuálne zľavy'), icon: Percent },
          { id: 'stats', label: tt('Štatistiky'), icon: BarChart3 },
        ]}
      />

      {activeTab === 'coupons' && (
        <div className="space-y-4">
          <FilterBar
            search={{
              value: couponSearch,
              onChange: setCouponSearch,
              placeholder: tt('Hľadať kód...'),
            }}
            filters={
              <FilterSelect
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: tt('Všetky') },
                  { value: 'active', label: tt('Aktívne') },
                  { value: 'inactive', label: tt('Neaktívne') },
                  { value: 'expired', label: tt('Expirované') },
                  { value: 'used_up', label: tt('Vyčerpané') },
                ]}
              />
            }
            actions={
              <button
                type="button"
                onClick={() => {
                  setEditingCoupon(null)
                  setCouponForm(emptyCouponForm())
                }}
                className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
              >
                <Plus className="w-4 h-4" /> <T>Nový kupón</T>
              </button>
            }
          />

          <DataTable
            columns={couponColumns}
            data={visibleCoupons}
            rowKey={(row) => row.id ?? row.code ?? ''}
            pageSize={20}
            emptyMessage={tt('Žiadne kupóny')}
          />
        </div>
      )}

      {activeTab === 'manual' && (
        <div className="space-y-4">
          <FilterBar
            actions={
              <button
                type="button"
                onClick={() => {
                  setEditingPreset(null)
                  setPresetForm({ label: '', type: 'percent', value: '', active: true })
                }}
                className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
              >
                <Plus className="w-4 h-4" /> <T>Nový preset</T>
              </button>
            }
          />

          <DataTable
            columns={presetColumns}
            data={presets ?? []}
            rowKey={(row) => row.id ?? row.label ?? ''}
            pageSize={20}
            emptyMessage={tt('Žiadne presety')}
          />

          {(stats?.manualHistory?.length ?? 0) > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="text-sm font-bold text-foreground mb-3">
                <T>Posledné manuálne zľavy</T>
              </h3>
              {/* §9.144 - six columns, because the endpoint sends six things and the strip that used
                * to be here drew three. A hundred-percent discount with no operator is not a log. */}
              <DataTable
                columns={manualHistoryColumns}
                data={stats?.manualHistory ?? []}
                rowKey={(row) => row.id ?? 0}
                pageSize={15}
                emptyMessage={tt('Zatiaľ neboli použité žiadne manuálne zľavy.')}
              />
            </div>
          )}
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void refetchStats()}
              disabled={isFetchingStats}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-muted/50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={cn('w-3.5 h-3.5', isFetchingStats && 'animate-spin')} />
              <T>Obnoviť</T>
            </button>
          </div>

          {/* §9.296 - the owner: "also things like… Filter based on day". Every figure below moves
            * with it, because the server applies the range to `orders.created_at`. */}
          <div className="flex flex-wrap items-center gap-2" data-coupon-period>
            {COUPON_PERIODS.map((key) => (
              <button
                key={key}
                type="button"
                data-period={key}
                aria-pressed={period === key}
                onClick={() => setPeriod(key)}
                className={cn(
                  'px-3 py-1.5 rounded-lg border text-xs font-semibold',
                  period === key
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border text-muted-foreground',
                )}
              >
                {couponPeriodLabel(key)}
              </button>
            ))}
            <input
              type="date"
              value={customRange.from}
              aria-label={tt('Od')}
              onChange={(event) => {
                setCustomRange({ ...customRange, from: event.target.value })
                setPeriod('custom')
              }}
              className="bg-background border border-border rounded-lg px-3 py-1.5 text-xs"
            />
            <input
              type="date"
              value={customRange.to}
              aria-label={tt('Do')}
              onChange={(event) => {
                setCustomRange({ ...customRange, to: event.target.value })
                setPeriod('custom')
              }}
              className="bg-background border border-border rounded-lg px-3 py-1.5 text-xs"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              label={tt('Použité kupóny')}
              value={stats?.couponCount ?? 0}
              icon={Tag}
              color="primary"
            />
            <StatCard
              label={tt('Zľavy z kupónov')}
              value={formatMoney(stats?.couponDiscountTotal)}
              icon={Banknote}
              color="amber"
            />
            <StatCard
              label={tt('Manuálne zľavy')}
              value={formatMoney(stats?.manualDiscountTotal)}
              icon={TrendingDown}
              color="red"
            />
            {/* §10A.173 - a discount with no coupon and no recorded operator. The coupon card used
              * to hold these, and it is labelled *Použité kupóny*. Drawn only when there are any,
              * so a franchise whose every discount is accounted for does not carry an empty card. */}
            {Number(stats?.otherDiscountTotal ?? 0) > 0 && (
              <StatCard
                label={tt('Ostatné zľavy')}
                value={formatMoney(stats?.otherDiscountTotal)}
                sub={`${stats?.otherDiscountCount ?? 0} ${tt('bez kupónu a bez obsluhy')}`}
                icon={TrendingDown}
                color="amber"
              />
            )}
            <StatCard
              label={tt('Objednávky so zľavou')}
              value={stats?.ordersWithDiscount ?? 0}
              sub={`${tt('priemer')} ${formatMoney(stats?.avgDiscountPerOrder)}`}
              icon={Users}
              color="blue"
            />
          </div>

          {/* §9.297 - legacy's *Rozdelenie zliav*, which the port left out. Both bars come from totals
            * the summary already answered. */}
          <div className="bg-card border border-border rounded-2xl p-5" data-discount-split>
            <h3 className="text-sm font-bold text-foreground mb-3">
              <T>Rozdelenie zliav</T>
            </h3>
            {(() => {
              const share = discountShare(
                Number(stats?.couponDiscountTotal ?? 0),
                Number(stats?.manualDiscountTotal ?? 0),
                // §10A.173 - the remainder, so the three bars account for the whole discount given
                // away. Without it the coupon bar was claiming 845 orders that had no coupon.
                Number(stats?.otherDiscountTotal ?? 0),
              )
              const bars = [
                { key: 'coupon', label: tt('Kupónové zľavy'), value: stats?.couponDiscountTotal, percent: share.couponPercent, colour: 'bg-primary' },
                { key: 'manual', label: tt('Manuálne zľavy'), value: stats?.manualDiscountTotal, percent: share.manualPercent, colour: 'bg-amber-500' },
                { key: 'other', label: tt('Ostatné zľavy'), value: stats?.otherDiscountTotal, percent: share.otherPercent, colour: 'bg-muted-foreground/40' },
              ]
              return (
                <div className="space-y-3">
                  {bars.map((bar) => (
                    <div key={bar.key} data-discount-bar={bar.key}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">{bar.label}</span>
                        <span className="font-semibold">{formatMoney(bar.value)}</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className={cn('h-full rounded-full transition-all', bar.colour)}
                          style={{ width: `${bar.percent}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )
            })()}
          </div>

          {/* §10A.101 - where the redeemed coupons came from. `unknown` is everything until campaigns
            * run with §10A.98 in place, and the note says so rather than leaving a puzzling bar. */}
          {(stats?.redemptionsByOrigin?.length ?? 0) > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5" data-origin-panel>
              <h3 className="text-sm font-bold text-foreground mb-3">
                <T>Pôvod uplatnených kupónov</T>
              </h3>
              <div className="space-y-1.5">
                {stats?.redemptionsByOrigin?.map((entry) => (
                  <div
                    key={entry.origin}
                    data-origin-row={entry.origin}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-muted/30 text-sm"
                  >
                    <span className="font-semibold">{originLabel(entry.origin ?? '')}</span>
                    <span className="text-muted-foreground">
                      {formatCount(entry.redemptions ?? 0)}×
                    </span>
                  </div>
                ))}
              </div>
              {stats?.redemptionsByOrigin?.some((entry) => entry.origin === 'unknown') === true && (
                <p className="text-[11px] text-muted-foreground mt-2" data-origin-note>
                  <T>
                    Pôvod sa eviduje od septembra 2026. Staršie kupóny ho nemajú - nie je to chyba
                    merania, len sa to vtedy nezaznamenávalo.
                  </T>
                </p>
              )}
            </div>
          )}

          {(stats?.topCoupons?.length ?? 0) > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="text-sm font-bold text-foreground mb-3">
                <T>Najpoužívanejšie kupóny</T>
              </h3>
              <DataTable
                columns={topCouponColumns}
                data={rankedTopCoupons}
                rowKey={(row) => row.couponId ?? row.rank}
                pageSize={20}
                emptyMessage={tt('Žiadne kupóny neboli doteraz použité.')}
              />
            </div>
          )}
        </div>
      )}

      {couponForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-lg font-bold">
                {editingCoupon ? tt('Upraviť kupón') : tt('Nový kupón')}
              </h2>
              <button
                type="button"
                aria-label={tt('Zatvoriť')}
                onClick={() => setCouponForm(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL_CLASSES} htmlFor="coupon-kod">
                    <T>Kód</T>
                  </label>
                  <input
                    id="coupon-kod"
                    value={couponForm.code}
                    onChange={(event) =>
                      setCouponForm({ ...couponForm, code: event.target.value })
                    }
                    className={cn(FIELD_CLASSES, 'font-mono uppercase')}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASSES} htmlFor="coupon-typ">
                    <T>Typ</T>
                  </label>
                  <select
                    id="coupon-typ"
                    value={couponForm.type}
                    onChange={(event) =>
                      setCouponForm({
                        ...couponForm,
                        type: event.target.value === 'percent' ? 'percent' : 'fixed',
                      })
                    }
                    className={FIELD_CLASSES}
                  >
                    <option value="percent">{tt('Percento (%)')}</option>
                    <option value="fixed">{tt('Pevná suma (€)')}</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL_CLASSES} htmlFor="coupon-hodnota">
                    <T>Hodnota</T>
                  </label>
                  <input
                    id="coupon-hodnota"
                    type="number"
                    step="0.01"
                    value={couponForm.value}
                    onChange={(event) =>
                      setCouponForm({ ...couponForm, value: event.target.value })
                    }
                    className={FIELD_CLASSES}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASSES} htmlFor="coupon-min-objednavka">
                    <T>Min. objednávka (€)</T>
                  </label>
                  <input
                    id="coupon-min-objednavka"
                    type="number"
                    step="0.01"
                    value={couponForm.minOrderAmount}
                    onChange={(event) =>
                      setCouponForm({ ...couponForm, minOrderAmount: event.target.value })
                    }
                    className={FIELD_CLASSES}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASSES} htmlFor="coupon-max-pouziti">
                    <T>Max. použití</T>
                  </label>
                  <input
                    id="coupon-max-pouziti"
                    type="number"
                    value={couponForm.maxUses}
                    onChange={(event) =>
                      setCouponForm({ ...couponForm, maxUses: event.target.value })
                    }
                    placeholder={tt('neobmedzené')}
                    className={FIELD_CLASSES}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASSES} htmlFor="coupon-platnost-do">
                    <T>Platnosť do</T>
                  </label>
                  <input
                    id="coupon-platnost-do"
                    type="date"
                    value={couponForm.expiresAt}
                    onChange={(event) =>
                      setCouponForm({ ...couponForm, expiresAt: event.target.value })
                    }
                    className={FIELD_CLASSES}
                  />
                </div>
              </div>

              <div>
                <label className={LABEL_CLASSES} htmlFor="coupon-minimalna-vernostna-uroven">
                  <T>Minimálna vernostná úroveň</T>
                </label>
                <select
                  id="coupon-minimalna-vernostna-uroven"
                  value={couponForm.minLoyaltyLevel}
                  onChange={(event) =>
                    setCouponForm({ ...couponForm, minLoyaltyLevel: event.target.value })
                  }
                  className={FIELD_CLASSES}
                >
                  <option value="">{tt('- pre všetkých -')}</option>
                  {LOYALTY_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={couponForm.active}
                  onChange={(event) =>
                    setCouponForm({ ...couponForm, active: event.target.checked })
                  }
                  className="w-4 h-4 rounded accent-primary"
                />
                <span className="text-sm">
                  <T>Aktívny</T>
                </span>
              </label>
            </div>

            <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setCouponForm(null)}
                className="px-4 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-muted/50"
              >
                <T>Zrušiť</T>
              </button>
              <button
                type="button"
                onClick={saveCoupon}
                disabled={createCoupon.isPending || updateCoupon.isPending}
                className={cn('px-5 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
              >
                <T>Uložiť</T>
              </button>
            </div>
          </div>
        </div>
      )}

      {presetForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-lg font-bold">
                {editingPreset ? tt('Upraviť preset') : tt('Nový preset')}
              </h2>
              <button
                type="button"
                aria-label={tt('Zatvoriť')}
                onClick={() => setPresetForm(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className={LABEL_CLASSES} htmlFor="coupon-popis">
                  <T>Popis</T>
                </label>
                <input
                  id="coupon-popis"
                  value={presetForm.label}
                  onChange={(event) => setPresetForm({ ...presetForm, label: event.target.value })}
                  placeholder={tt('napr. Zľava pre zamestnanca')}
                  className={FIELD_CLASSES}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL_CLASSES} htmlFor="coupon-typ-2">
                    <T>Typ</T>
                  </label>
                  <select
                    id="coupon-typ-2"
                    value={presetForm.type}
                    onChange={(event) =>
                      setPresetForm({
                        ...presetForm,
                        type: event.target.value === 'percent' ? 'percent' : 'fixed',
                      })
                    }
                    className={FIELD_CLASSES}
                  >
                    <option value="percent">{tt('Percento (%)')}</option>
                    <option value="fixed">{tt('Pevná suma (€)')}</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL_CLASSES} htmlFor="coupon-hodnota-2">
                    <T>Hodnota</T>
                  </label>
                  <input
                    id="coupon-hodnota-2"
                    type="number"
                    step="0.01"
                    value={presetForm.value}
                    onChange={(event) =>
                      setPresetForm({ ...presetForm, value: event.target.value })
                    }
                    className={FIELD_CLASSES}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={presetForm.active}
                  onChange={(event) =>
                    setPresetForm({ ...presetForm, active: event.target.checked })
                  }
                  className="w-4 h-4 rounded accent-primary"
                />
                <span className="text-sm">
                  <T>Aktívny</T>
                </span>
              </label>
            </div>

            <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPresetForm(null)}
                className="px-4 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-muted/50"
              >
                <T>Zrušiť</T>
              </button>
              <button
                type="button"
                onClick={savePreset}
                disabled={createPreset.isPending || updatePreset.isPending}
                className={cn('px-5 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
              >
                <T>Uložiť</T>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FB-028: legacy asks with confirm() for both. The coupon dialog names
          the usage count - deleting a redeemed coupon is a different decision. */}
      <ConfirmDialog
        open={pendingCouponDeletion !== null}
        onClose={() => setPendingCouponDeletion(null)}
        onConfirm={() => {
          const couponId = pendingCouponDeletion?.id
          if (couponId === undefined) return
          removeCoupon.mutate(
            { couponId },
            {
              onSuccess: () => {
                void refreshCoupons()
                setPendingCouponDeletion(null)
                toast({ title: tt('Kupón zmazaný') })
              },
            },
          )
        }}
        loading={removeCoupon.isPending}
        variant="danger"
        title={tt('Zmazať kupón?')}
        description={`${pendingCouponDeletion?.code ?? ''} - ${tt('použitý')} ${pendingCouponDeletion?.usedCount ?? 0}×. ${tt('Zmazanie je nezvratné.')}`}
        confirmLabel={tt('Zmazať')}
      />

      <ConfirmDialog
        open={pendingPresetDeletion !== null}
        onClose={() => setPendingPresetDeletion(null)}
        onConfirm={() => {
          const presetId = pendingPresetDeletion?.id
          if (presetId === undefined) return
          removePreset.mutate(
            { presetId },
            {
              onSuccess: () => {
                void refreshPresets()
                setPendingPresetDeletion(null)
                toast({ title: tt('Preset zmazaný') })
              },
            },
          )
        }}
        loading={removePreset.isPending}
        variant="danger"
        title={tt('Zmazať preset?')}
        description={`${tt('Preset')} "${pendingPresetDeletion?.label ?? ''}" ${tt('sa prestane ponúkať na kase.')}`}
        confirmLabel={tt('Zmazať')}
      />
    </div>
  )
}
